import { useToast, type DataTableProps, type MenuItem } from '@ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../lib/errors';

/**
 * Builds the `…` menu of table and list rows (#197): the shared labels for
 * the column, the button and the usual items. `DataTable` and `ListRow` put
 * safe actions first and destructive ones last, set apart and red.
 */
export function useRowMenu() {
  const { t } = useTranslation();
  const label = (name: string) => `${t('common.moreActions')}: ${name}`;
  return {
    label,
    edit: (onSelect: () => void): MenuItem => ({
      key: 'edit',
      label: t('common.edit'),
      onSelect,
    }),
    remove: (onSelect: () => void, text = t('common.delete')): MenuItem => ({
      key: 'delete',
      label: text,
      tone: 'danger',
      onSelect,
    }),
    /** The `rowActions` of a `DataTable`. */
    table: <T>(
      name: (row: T) => string,
      items: (row: T) => (MenuItem | false | null | undefined)[],
    ): NonNullable<DataTableProps<T>['rowActions']> => ({
      header: t('common.actions'),
      label: (row) => label(name(row)),
      items,
    }),
  };
}

/**
 * The record a row-menu action loads before its dialog opens (#201). Until it
 * is there the dialog stays closed; if loading fails, a toast says why and
 * the action ends, so the click never goes without a visible effect.
 */
export function useLoadedForAction<T>(
  query: { data: T | undefined; error: unknown },
  onClose: () => void,
): T | undefined {
  const toast = useToast();
  const close = useRef(onClose);
  close.current = onClose;
  // A failed refetch keeps the record loaded, and the dialog stays open.
  const failed = query.data === undefined ? query.error : null;
  useEffect(() => {
    if (!failed) return;
    toast.show(errorMessage(failed), 'danger');
    close.current();
  }, [failed, toast]);
  return query.data;
}
